// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Settings.js
// User settings: lists, annotations, chart indicator favourites (Chart 2), Discord alerts (Connect Discord), account preferences.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';
import RankingEditor from '../components/RankingEditor';
import { INDICATORS } from '../chart/indicators';

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:      { minHeight: '100vh', background: 'var(--bg)' },
  nav:       { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 32px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo:      { fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: 500, letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase' },
  navRight:  { display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  navLink:   { color: 'var(--accent)', fontSize: '13px', textDecoration: 'none' },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", padding: '6px 12px', borderRadius: 'var(--radius)' },

  content:   { maxWidth: '900px', margin: '0 auto', padding: '48px 32px' },
  pageTitle: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '4px' },
  pageSub:   { fontSize: '14px', color: 'var(--text-muted)', marginBottom: '48px' },

  section:      { marginBottom: '48px' },
  sectionHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '2px solid var(--border)', paddingBottom: '10px' },
  sectionTitle: { fontSize: '16px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' },

  card:    { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '20px', marginBottom: '12px' },
  cardRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' },
  cardInfo:{ flex: 1 },
  cardName:{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' },
  cardMeta:{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace" },

  btn:       { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '7px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSm:     { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnGhost:  { background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnDanger: { background: 'none', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' },

  input:    { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", boxSizing: 'border-box', resize: 'vertical', minHeight: '60px' },
  label:    { fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", display: 'block', marginBottom: '4px' },
  formRow:  { marginBottom: '12px' },
  select:   { padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Mono',monospace" },

  searchWrap: { position: 'relative' },
  dropdown:   { position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 200, maxHeight: '220px', overflowY: 'auto' },
  dropItem:   { padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid var(--border)', fontFamily: "'DM Mono',monospace" },

  tableWrap: { border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginTop: '8px' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'DM Mono',monospace" },
  th:        { padding: '7px 10px', textAlign: 'left', borderBottom: '2px solid var(--border)', background: 'var(--accent)', color: '#fff', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' },
  td:        { padding: '7px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },

  tickerLink: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 600, fontFamily: "'DM Mono',monospace", fontSize: '12px', textDecoration: 'underline' },

  badge: (type) => ({
    fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 700,
    background: type === 'etf' ? '#dbeafe' : type === 'Public' ? '#dcfce7' : type === 'Share' ? '#fef9c3' : '#f3f4f6',
    color: type === 'etf' ? '#1d4ed8' : type === 'Public' ? '#15803d' : type === 'Share' ? '#854d0e' : '#6b7280',
  }),

  empty:   { padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' },
  error:   { color: '#dc2626', fontSize: '12px', marginTop: '4px' },
  success: { color: '#16a34a', fontSize: '12px', marginTop: '4px' },

  expandBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '11px', fontFamily: "'DM Mono',monospace", padding: '0 4px' },
  divider:   { border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' },
  loadingScreen: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted)', fontSize: '13px' },

  // Share row inside annotation expand
  shareRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  shareUser: { fontSize: '12px', fontFamily: "'DM Mono',monospace", flex: 1, color: 'var(--text)' },
};

// ── Open ticker in new tab ────────────────────────────────────────────────────
function openChart(ticker) {
  window.open(`/chart?t=${encodeURIComponent(ticker)}`, '_blank');
}

// ── Ticker search input with autocomplete ─────────────────────────────────────
function TickerSearch({ onSelect, placeholder = 'Search ticker…' }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const timer   = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (!val.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get('/lists/instruments/search', { params: { q: val } })
        .then(r => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
  };

  const pick = (item) => { onSelect(item); setQ(''); setResults([]); setOpen(false); };

  return (
    <div ref={wrapRef} style={S.searchWrap}>
      <input style={S.input} value={q} onChange={e => search(e.target.value)} placeholder={placeholder} autoComplete="off" />
      {loading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--text-muted)' }}>…</span>}
      {open && results.length > 0 && (
        <div style={S.dropdown}>
          {results.map(r => (
            <div key={r.instrument_id} style={S.dropItem}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => pick(r)}
            >
              <span style={{ fontWeight: 600 }}>{r.symbol}:{r.exchange}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{r.name}</span>
              <span style={S.badge(r.asset_type)}>{r.asset_type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Username search input with autocomplete ───────────────────────────────────
function UserSearch({ onSelect, placeholder = 'Search username…' }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const timer   = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get('/users/search', { params: { q: val } })
        .then(r => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
  };

  const pick = (item) => { onSelect(item); setQ(''); setResults([]); setOpen(false); };

  return (
    <div ref={wrapRef} style={S.searchWrap}>
      <input style={{ ...S.input, width: '200px' }} value={q} onChange={e => search(e.target.value)} placeholder={placeholder} autoComplete="off" />
      {loading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--text-muted)' }}>…</span>}
      {open && results.length > 0 && (
        <div style={{ ...S.dropdown, width: '200px' }}>
          {results.map(r => (
            <div key={r.id} style={S.dropItem}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => pick(r)}
            >
              {r.username}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Annotation share manager — shown inline when privacy = Share ──────────────
function ShareManager({ ann }) {
  const [shares,  setShares]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/annotations/shares', { params: { id: ann.id } })
      .then(r => setShares(r.data.shares || []))
      .catch(() => setShares([]))
      .finally(() => setLoading(false));
  }, [ann.id]);

  const addUser = async (user) => {
    try {
      await api.post('/annotations/shares/add', { annotation_id: ann.id, username: user.username });
      setShares(prev => [...(prev || []), { id: user.id, username: user.username, shared_at: new Date().toISOString() }]);
      setErr('');
    } catch (e) { setErr(e.response?.data?.error || 'Failed to add user'); }
  };

  const removeUser = async (userId) => {
    try {
      await api.post('/annotations/shares/remove', { annotation_id: ann.id, user_id: userId });
      setShares(prev => prev.filter(s => s.id !== userId));
    } catch { /* ignore */ }
  };

  if (loading) return <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '4px 0' }}>Loading shares…</div>;

  return (
    <div style={{ marginTop: '8px', padding: '10px', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: '8px' }}>Shared With</div>
      {shares && shares.length > 0 ? shares.map(s => (
        <div key={s.id} style={S.shareRow}>
          <span style={S.shareUser}>{s.username}</span>
          <button style={S.btnDanger} onClick={() => removeUser(s.id)}>Remove</button>
        </div>
      )) : (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>No users added yet.</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <UserSearch onSelect={addUser} placeholder="Add username…" />
      </div>
      {err && <div style={S.error}>{err}</div>}
    </div>
  );
}

// ── Create List form ──────────────────────────────────────────────────────────
function CreateListForm({ onCreated }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [err,  setErr]  = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.post('/lists', { name: name.trim(), description: desc.trim() || null });
      onCreated(r.data.list);
      setName(''); setDesc('');
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to create list');
    } finally { setBusy(false); }
  };

  return (
    <div style={S.card}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>New List</div>
      <div style={S.formRow}>
        <label style={S.label}>Name</label>
        <input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. ASX Watchlist" maxLength={100} />
      </div>
      <div style={S.formRow}>
        <label style={S.label}>Description (optional)</label>
        <textarea style={S.textarea} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's this list for?" />
      </div>
      {err && <div style={S.error}>{err}</div>}
      <button style={S.btn} onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create List'}</button>
    </div>
  );
}

// ── Single list card ──────────────────────────────────────────────────────────
function ListCard({ list, onDeleted, onUpdated }) {
  const [expanded,     setExpanded]     = useState(false);
  const [showRanking,  setShowRanking]  = useState(false);
  const [items,        setItems]        = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing,      setEditing]      = useState(false);
  const [editName,     setEditName]     = useState(list.name);
  const [editDesc,     setEditDesc]     = useState(list.description || '');
  const [editErr,      setEditErr]      = useState('');
  const [deleting,     setDeleting]     = useState(false);
  const [msg,          setMsg]          = useState('');

  const loadItems = useCallback(() => {
    if (items !== null) return;
    setLoadingItems(true);
    api.get(`/lists/${list.id}/items`)
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoadingItems(false));
  }, [list.id, items]);

  const toggle = () => { if (!expanded) loadItems(); setExpanded(v => !v); };

  const saveEdit = async () => {
    if (!editName.trim()) { setEditErr('Name required'); return; }
    try {
      const r = await api.patch(`/lists/${list.id}`, { name: editName.trim(), description: editDesc.trim() || null });
      onUpdated(r.data.list);
      setEditing(false); setEditErr('');
    } catch (e) { setEditErr(e.response?.data?.error || 'Failed to save'); }
  };

  const deleteList = async () => {
    if (!window.confirm(`Delete list "${list.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await api.delete(`/lists/${list.id}`); onDeleted(list.id); }
    catch { setDeleting(false); }
  };

  const addTicker = async (instrument) => {
    try {
      const r = await api.post(`/lists/${list.id}/items`, { ticker: instrument.ticker });
      setItems(prev => [...(prev || []), r.data.item]);
      onUpdated({ ...list, item_count: (list.item_count || 0) + 1 });
      setMsg('Added'); setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      // 409 = already on list, treat as success
      if (e.response?.status === 409) { setMsg('Already on list'); setTimeout(() => setMsg(''), 2000); }
      else { setMsg(e.response?.data?.error || 'Error'); setTimeout(() => setMsg(''), 2000); }
    }
  };

  const removeTicker = async (item) => {
    try {
      await api.delete(`/lists/${list.id}/items/${item.instrument_id}`);
      setItems(prev => prev.filter(i => i.instrument_id !== item.instrument_id));
      onUpdated({ ...list, item_count: Math.max(0, (list.item_count || 0) - 1) });
    } catch { /* ignore */ }
  };

  return (
    <div style={S.card}>
      {editing ? (
        <>
          <div style={S.formRow}>
            <label style={S.label}>Name</label>
            <input style={S.input} value={editName} onChange={e => setEditName(e.target.value)} maxLength={100} />
          </div>
          <div style={S.formRow}>
            <label style={S.label}>Description</label>
            <textarea style={S.textarea} value={editDesc} onChange={e => setEditDesc(e.target.value)} />
          </div>
          {editErr && <div style={S.error}>{editErr}</div>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={S.btn} onClick={saveEdit}>Save</button>
            <button style={S.btnGhost} onClick={() => { setEditing(false); setEditName(list.name); setEditDesc(list.description || ''); }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div style={S.cardRow}>
            <div style={S.cardInfo}>
              <div style={S.cardName}>{list.name}</div>
              {list.description && <div style={S.cardMeta}>{list.description}</div>}
              <div style={{ ...S.cardMeta, marginTop: '4px' }}>{list.item_count} ticker{list.item_count !== 1 ? 's' : ''} · {list.privacy_level}</div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
              <button style={S.expandBtn} onClick={toggle}>{expanded ? '▲ Hide' : '▼ Tickers'}</button>
              <button style={S.btnGhost} onClick={() => setShowRanking(v => !v)}>{showRanking ? '✕ Ranking' : '⚙ Ranking'}</button>
              <button style={S.btnGhost} onClick={() => setEditing(true)}>Edit</button>
              <button style={S.btnDanger} onClick={deleteList} disabled={deleting}>Delete</button>
            </div>
          </div>

          {showRanking && <RankingEditor listId={list.id} />}

          {expanded && (
            <>
              <hr style={S.divider} />
              <div style={{ marginBottom: '10px' }}>
                <label style={S.label}>Add Ticker</label>
                <TickerSearch onSelect={addTicker} placeholder="Search to add…" />
                {msg && <div style={S.success}>{msg}</div>}
              </div>
              {loadingItems ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Loading…</div>
              ) : items && items.length > 0 ? (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Ticker</th>
                        <th style={S.th}>Name</th>
                        <th style={S.th}>Type</th>
                        <th style={S.th}>Exch</th>
                        <th style={S.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.instrument_id}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={S.td}>
                            <button style={S.tickerLink} onClick={() => openChart(item.ticker)}>{item.symbol}</button>
                          </td>
                          <td style={{ ...S.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name || '—'}</td>
                          <td style={S.td}><span style={S.badge(item.asset_type)}>{item.asset_type}</span></td>
                          <td style={S.td}>{item.exchange}</td>
                          <td style={S.td}>
                            <button style={S.btnDanger} onClick={() => removeTicker(item)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={S.empty}>No tickers on this list yet.</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Discord section ───────────────────────────────────────────────────────────
// Connect Discord so the bot sends this user's alerts to them privately (backend/discord_link.py).
// /settings?discord=ok|error&why=... is where the Discord sign-in comes back to.
// eslint-disable-next-line no-unused-vars
function DiscordSection() {
  const [st, setSt] = useState(null);
  const [msg, setMsg] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get('discord') === 'ok' ? 'Connected - check Discord for a message from the bot.'
         : q.get('discord') === 'error' ? `Couldn't connect: ${q.get('why') || 'unknown error'}` : '';
  });
  const load = () => api.get('/discord/status').then(r => setSt(r.data)).catch(() => setSt({ linked: false }));
  useEffect(() => { load(); }, []);
  if (!st) return <div style={S.empty}>Loading…</div>;
  const test = async () => {
    setMsg('Sending…');
    try { const r = await api.post('/discord/test'); setMsg(r.data.ok ? 'Test message sent.' : `Not delivered: ${r.data.error}`); }
    catch (_) { setMsg('Not delivered.'); }
    load();
  };
  const toggle = async on => { await api.post('/discord/settings', { alerts_enabled: on }).catch(() => {}); load(); };
  const unlink = async () => { if (!window.confirm('Disconnect Discord? Alerts will still appear under the bell.')) return; await api.delete('/discord/link').catch(() => {}); setMsg(''); load(); };
  return (
    <div style={S.card}>
      <div style={{ ...S.cardMeta, marginBottom: '12px' }}>
        Get your chart alerts as private messages from the woody.wtf bot. Connecting adds you to the woody.wtf Discord server
        (the bot can only message people it shares a server with). Alerts always appear under the 🔔 bell as well.
      </div>
      {!st.linked ? (
        st.configured === false
          ? <div style={S.cardMeta}>Discord sign-in isn't set up on the server yet.</div>
          : <button style={S.btn} onClick={() => { window.location.href = '/api/discord/connect'; }}>Connect Discord</button>
      ) : (
        <>
          <div style={S.cardName}>Connected as {st.name}</div>
          <div style={{ ...S.cardMeta, margin: '4px 0 10px', color: st.dm_ok === false ? '#dc2626' : 'var(--text-muted)' }}>
            {st.dm_ok === false ? `Can't deliver: ${st.dm_error || 'Discord refused the message'}` : st.dm_ok ? 'Messages are getting through.' : 'No message sent yet.'}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={st.alerts_enabled} onChange={e => toggle(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
            Send my alerts to Discord
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={S.btnSm} onClick={test}>Send test message</button>
            <button style={S.btnGhost} onClick={() => { window.location.href = '/api/discord/connect'; }}>Reconnect</button>
            <button style={S.btnDanger} onClick={unlink}>Disconnect</button>
          </div>
        </>
      )}
      {msg && <div style={{ ...S.cardMeta, marginTop: '10px', color: msg.startsWith('Couldn') || msg.startsWith('Not') ? '#dc2626' : 'var(--accent)' }}>{msg}</div>}
    </div>
  );
}

// ── Chart indicators section ──────────────────────────────────────────────────
// Ticked indicators show first in Chart 2's indicator picker. Saved in the user's chart
// prefs (/api/users/chart-prefs), keeping the default layout saved from the chart.
function ChartIndicatorsSection() {
  const [prefs, setPrefs] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.get('/users/chart-prefs').then(r => setPrefs({ favourites: [], ...(r.data.prefs || {}) })).catch(() => setPrefs({ favourites: [] }));
  }, []);
  if (!prefs) return <div style={S.empty}>Loading…</div>;
  const fav = new Set(prefs.favourites || []);
  const toggle = async id => {
    const next = { ...prefs, favourites: fav.has(id) ? [...fav].filter(x => x !== id) : [...fav, id] };
    setPrefs(next);
    try { await api.put('/users/chart-prefs', { prefs: next }); setMsg('Saved'); } catch (_) { setMsg('Could not save'); }
    setTimeout(() => setMsg(''), 1500);
  };
  const group = pane => Object.entries(INDICATORS).filter(([, d]) => d.pane === pane);
  return (
    <div style={S.card}>
      <div style={{ ...S.cardMeta, marginBottom: '12px' }}>Tick the indicators you use most. They appear first in the chart's indicator list. {msg && <b style={{ color: 'var(--accent)' }}>{msg}</b>}</div>
      {[['On price', 'price'], ['Own pane', 'own']].map(([title, pane]) => (
        <div key={pane} style={{ marginBottom: '12px' }}>
          <span style={S.label}>{title}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 12px' }}>
            {group(pane).map(([id, d]) => (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={fav.has(id)} onChange={() => toggle(id)} style={{ accentColor: 'var(--accent)' }} />
                {d.name}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Annotations section ───────────────────────────────────────────────────────
function AnnotationsSection() {
  const [mine,       setMine]       = useState(null);
  const [sharedWith, setSharedWith] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState({}); // ann.id -> bool for share manager

  useEffect(() => {
    Promise.all([
      api.get('/annotations/list'),
      api.get('/annotations/shared-with-me'),
    ]).then(([a, b]) => {
      setMine(a.data.annotations || []);
      setSharedWith(b.data.annotations || []);
    }).catch(() => {
      setMine([]); setSharedWith([]);
    }).finally(() => setLoading(false));
  }, []);

  const updatePrivacy = async (ann, level) => {
    try {
      await api.post('/annotations/privacy', { annotation_id: ann.id, privacy_level: level });
      setMine(prev => prev.map(a => a.id === ann.id ? { ...a, privacy_level: level } : a));
      // Collapse share manager if switching away from Share
      if (level !== 'Share') setExpanded(prev => ({ ...prev, [ann.id]: false }));
    } catch { /* ignore */ }
  };

  const deleteAnn = async (ann) => {
    if (!window.confirm(`Delete annotations for ${ann.ticker}? This cannot be undone.`)) return;
    try {
      await api.delete('/annotations/delete', { params: { id: ann.id } });
      setMine(prev => prev.filter(a => a.id !== ann.id));
    } catch { /* ignore */ }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>;

  return (
    <>
      {/* My annotations */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>My Annotations</div>
        {mine.length === 0 ? (
          <div style={S.empty}>You have no saved annotations.</div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Ticker</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Privacy</th>
                  <th style={S.th}>Last Edited</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {mine.map(ann => (
                  <React.Fragment key={ann.id}>
                    <tr
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <td style={S.td}>
                        <button style={S.tickerLink} onClick={() => openChart(ann.ticker)}>{ann.ticker}</button>
                      </td>
                      <td style={S.td}>{ann.chart_type}</td>
                      <td style={S.td}>
                        <select style={S.select} value={ann.privacy_level} onChange={e => updatePrivacy(ann, e.target.value)}>
                          <option value="Private">Private</option>
                          <option value="Public">Public</option>
                          <option value="Share">Share</option>
                        </select>
                        {ann.privacy_level === 'Share' && (
                          <button style={{ ...S.expandBtn, marginLeft: '6px' }} onClick={() => toggleExpand(ann.id)}>
                            {expanded[ann.id] ? '▲' : '▼ Manage'}
                          </button>
                        )}
                      </td>
                      <td style={S.td}>{ann.last_edited ? new Date(ann.last_edited).toLocaleDateString() : '—'}</td>
                      <td style={S.td}>
                        <button style={S.btnDanger} onClick={() => deleteAnn(ann)}>Delete</button>
                      </td>
                    </tr>
                    {ann.privacy_level === 'Share' && expanded[ann.id] && (
                      <tr>
                        <td colSpan={5} style={{ ...S.td, background: 'var(--bg)' }}>
                          <ShareManager ann={ann} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shared with me */}
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>Shared With Me</div>
        {sharedWith.length === 0 ? (
          <div style={S.empty}>No annotations have been shared with you.</div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Ticker</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Shared By</th>
                  <th style={S.th}>Shared At</th>
                </tr>
              </thead>
              <tbody>
                {sharedWith.map(ann => (
                  <tr key={ann.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={S.td}>
                      <button style={S.tickerLink} onClick={() => openChart(ann.ticker)}>{ann.ticker}</button>
                    </td>
                    <td style={S.td}>{ann.chart_type}</td>
                    <td style={S.td}>{ann.shared_by}</td>
                    <td style={S.td}>{ann.shared_at ? new Date(ann.shared_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Settings page ─────────────────────────────────────────────────────────────
export default function Settings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [lists,       setLists]       = useState([]);
  const [listsLoaded, setListsLoaded] = useState(false);

  useEffect(() => { document.title = 'Settings | woody.wtf'; }, []);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get('/lists')
      .then(r => setLists(r.data.lists || []))
      .catch(() => {})
      .finally(() => setListsLoaded(true));
  }, [user]);

  const handleListCreated = (list)    => setLists(prev => [...prev, list]);
  const handleListDeleted = (id)      => setLists(prev => prev.filter(l => l.id !== id));
  const handleListUpdated = (updated) => setLists(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } : l));

  if (loading || !user) return <div style={S.loadingScreen}>Loading…</div>;

  return (
    <div style={S.page}>
      <Navbar />

      <div style={S.content}>
        <h1 style={S.pageTitle}>Settings</h1>
        <p style={S.pageSub}>Manage your lists, annotations, and account preferences.</p>

        {/* ── My Lists ── */}
        <div style={S.section}>
          <div style={S.sectionHead}>
            <span style={S.sectionTitle}>My Lists</span>
          </div>
          <CreateListForm onCreated={handleListCreated} />
          {listsLoaded && lists.length === 0 && (
            <div style={S.empty}>You have no lists yet. Create one above.</div>
          )}
          {lists.map(list => (
            <ListCard key={list.id} list={list} onDeleted={handleListDeleted} onUpdated={handleListUpdated} />
          ))}
        </div>

        {/* ── Annotations ── */}
        <div style={S.section}>
          <div style={S.sectionHead}>
            <span style={S.sectionTitle}>Annotations</span>
          </div>
          <AnnotationsSection />
        </div>

        {/* Discord Alerts (DiscordSection) is parked: alerts go to the #custom-alerts channel for now. */}

        {/* ── Chart indicators ── */}
        <div style={S.section}>
          <div style={S.sectionHead}>
            <span style={S.sectionTitle}>Chart Indicators</span>
          </div>
          <ChartIndicatorsSection />
        </div>

        {/* ── Account ── */}
        <div style={S.section}>
          <div style={S.sectionHead}>
            <span style={S.sectionTitle}>Account</span>
          </div>
          <div style={{ ...S.card, color: 'var(--text-muted)', fontSize: '13px' }}>
            Account settings coming soon.
          </div>
        </div>
      </div>
    </div>
  );
}
